import { API_URL } from "../../constants/constants";
import { ResponseDataType, listDataType } from "../../types";
import { apiSlice } from "../api/apiSlice";

const apiWithTags = apiSlice.enhanceEndpoints({
  addTagTypes: [
    "organizationList",
    "organizationDetails",
    "organizationMembers",
    "organizationsPricing",
    "mitutoyoProjectList",
    "mitutoyoProjectDetails",
    "projectList",
    "mitutoyoProjectMembers",
    "organizationOptions",
    "mitutoyoDepartmentList",
    "mitutoyoDepartmentDetails",
    "mitutoyoDepartmentMembers",
    "mitutoyoSectionList",
    "mitutoyoSectionDetails",
    "mitutoyoSectionMembers",
  ],
});

export const organizationSlice = apiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Fetches a list of organizations from the server.
     *
     * @param search - The search query to filter organizations.
     * @param limit - The maximum number of organizations to retrieve.
     * @param current_page - The current page number for pagination.
     * @returns A promise that resolves to the response data containing the list of organizations.
     */
    organizationList: builder.query<ResponseDataType, listDataType>({
      query: ({ search, limit, current_page }) => ({
        url:
          API_URL.ORGANIZATIONS +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["organizationList"],
    }),

    organizationOptions: builder.query<ResponseDataType, void>({
      query: () => ({
        url: API_URL.ORGANIZATIONS + "/options",
        method: "GET",
      }),
      providesTags: ["organizationOptions"],
    }),

    /**
     * Fetches organization details from the server.
     *
     * @param id - The ID of the organization to fetch.
     * @returns A promise that resolves to the organization details.
     */
    organizationDetails: builder.query<ResponseDataType, number>({
      query: (id) => ({
        url: API_URL.ORGANIZATIONS + `/${id}`,
        method: "GET",
      }),
      providesTags: ["organizationDetails"],
    }),

    organizationMembers: builder.query<ResponseDataType, listDataType>({
      query: ({ id, search, limit, current_page }) => ({
        url: API_URL.ORGANIZATIONS + `/${id}/members` +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["organizationMembers"],
    }),

    /**
     * Creates a new organization.
     * @param formData - The form data containing the organization details.
     * @returns A promise that resolves to the response data.
     */
    organizationCreate: builder.mutation<ResponseDataType, FormData>({
      query: (formData) => ({
        url: API_URL.ORGANIZATIONS,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "organizationList",
        "organizationDetails",
        "organizationsPricing",
        "organizationOptions",
        "projectList",
      ],
    }),

    /**
     * Updates an organization.
     *
     * @param id - The ID of the organization to update.
     * @param formData - The form data containing the updated organization details.
     * @returns A promise that resolves to the updated organization data.
     */
    organizationUpdate: builder.mutation<
      ResponseDataType,
      { id: number; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: API_URL.ORGANIZATIONS + `/${id}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [
        "organizationList",
        "organizationDetails",
        "organizationsPricing",
      ],
    }),

    /**
     * Deletes an organization.
     *
     * @param id - The ID of the organization to delete.
     * @returns A promise that resolves to the response data.
     */
    organizationDelete: builder.mutation<ResponseDataType, number>({
      query: (id) => ({
        url: API_URL.ORGANIZATIONS + `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "organizationList",
        "organizationDetails",
        "organizationsPricing",
      ],
    }),


    ProjectList: builder.query<ResponseDataType, listDataType>({
      query: ({ search, limit, current_page }) => ({
        url:
          API_URL.PROJECTS +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["projectList"],
    }),
    /**
     * Fetches a list of organizations with pricing details.
     * @returns A promise that resolves to the response data containing the list of organizations with pricing details.
     */

    mitutoyoProjectList: builder.query<ResponseDataType, listDataType>({
      query: ({ search, limit, current_page }) => ({
        url:
          API_URL.PROJECTS +
          "/tool-wise-members" +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
        method: "GET",
      }),
      providesTags: ["mitutoyoProjectList"],
    }),

    /**
     * Fetches a single project from the server.
     *
     * @param id - The ID of the project to retrieve.
     * @returns A promise that resolves to the response data containing the project.
     */

    mitutoyoProjectDetails: builder.query<ResponseDataType, number>({
      query: (id) => ({
        url: API_URL.PROJECTS + `/${id}`,
        method: "GET",
      }),
      providesTags: ["mitutoyoProjectDetails"],
    }),

    mitutoyoProjectMembers: builder.query<ResponseDataType, listDataType>({
      query: ({ id, search, limit, current_page, sort_by, sort_direction }) => ({
        url: API_URL.PROJECTS + `/${id}/members` +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""}`,
        method: "GET",
      }),
      providesTags: ["mitutoyoProjectMembers"],
    }),

    mitutoyoDepartmentList: builder.query<ResponseDataType, listDataType>({
      query: ({ search, limit, current_page, sort_by, sort_direction }) => ({
        url:
          API_URL.ORGANIZATIONS + API_URL.DEPARTMENTS +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""}`,
        method: "GET",
      }),
      providesTags: ["mitutoyoDepartmentList"],
    }),
    mitutoyoDepartmentDetails: builder.query<ResponseDataType, string>({
      query: (name) => ({
        url: API_URL.ORGANIZATIONS + API_URL.DEPARTMENTS + `/${name}`,
        method: "GET",
      }),
      providesTags: ["mitutoyoDepartmentDetails"],
    }),

    mitutoyoDepartmentMembers: builder.query<ResponseDataType, listDataType>({
      query: ({ id: name, search, limit, current_page, sort_by, sort_direction }) => ({
        url: API_URL.ORGANIZATIONS + API_URL.DEPARTMENTS + `/${name}/members` +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""}`,
        method: "GET",
      }),
      providesTags: ["mitutoyoDepartmentMembers"],
    }),

    MitutoyoSectionList: builder.query<ResponseDataType, listDataType>({
      query: ({ search, limit, current_page, sort_by, sort_direction }) => ({
        url:
          API_URL.ORGANIZATIONS + API_URL.SECTIONS +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""}`,
        method: "GET",
      }),
      providesTags: ["mitutoyoSectionList"],
    }),

    MitutoyoSectionDetails: builder.query<ResponseDataType, string>({
      query: (name) => ({
        url: API_URL.ORGANIZATIONS + API_URL.SECTIONS + `/${name}`,
        method: "GET",
      }),
      providesTags: ["mitutoyoSectionDetails"],
    }),

    MitutoyoSectionMembers: builder.query<ResponseDataType, listDataType>({
      query: ({ id: name, search, limit, current_page, sort_by, sort_direction }) => ({
        url: API_URL.ORGANIZATIONS + API_URL.SECTIONS + `/${name}/members` +
          `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
          }${search ? `&search=${encodeURIComponent(search)}` : ""}${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""}`,
        method: "GET",
      }),
      providesTags: ["mitutoyoSectionMembers"],
    })

  }),
});

export const {
  useOrganizationListQuery,
  useOrganizationDetailsQuery,
  useOrganizationMembersQuery,
  useOrganizationCreateMutation,
  useOrganizationUpdateMutation,
  useOrganizationDeleteMutation,
  useProjectListQuery,
  useMitutoyoProjectMembersQuery,
  useMitutoyoProjectListQuery,
  useMitutoyoProjectDetailsQuery,
  useOrganizationOptionsQuery,
  useMitutoyoDepartmentListQuery,
  useMitutoyoDepartmentDetailsQuery,
  useMitutoyoDepartmentMembersQuery,
  useMitutoyoSectionListQuery,
  useMitutoyoSectionDetailsQuery,
  useMitutoyoSectionMembersQuery,
} = organizationSlice;
